import React, { useEffect, useState, FC } from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'redux/app/store';
import { useDispatch } from 'react-redux';
import { Box  } from '@mui/material';
import dayjs from 'dayjs';
import axios, { API } from 'lib/config/axios';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { setLoading, setSnackbar } from 'redux/features/ui-slice';
import { WIDGET_KEYS } from 'views/deals-module/DealView/components/TourPackageWidget/utils/constants';
import useHandleNavigationToRPPage from 'views/common/hooks/useHandleNavigationToRPPage';
import useCookies from 'views/common/hooks/useCookies';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { DATE_FORMAT_ISO }  from 'views/rebranded-module/common/utils/dateFormate';
import { useTranslation } from 'next-i18next';

const TourPackageWidget = dynamic(() =>
  import(
    'views/rebranded-module/deals-module/DealDetailsViewV2/components/TourPackageWidget'
  ).then((file) => file.TourPackageWidget),
);

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const TourPackageWidgetWrapper:FC<any> = (props) => {
  const { dealTours, deal } = props;
  const content = useContent('DealDetailsPage');
  const dispatch = useDispatch();
  const handleErrorResponse = useErrorsHandler();
  const appState = useSelector((state) => state.globalData.appState);
  const isCrawler = useSelector((state) => state.globalData.isCrawler);
  const { navigateToNextPage } = useHandleNavigationToRPPage();
  const [, setCookie] = useCookies();
  const [widgetData, setWidgetData] = useState<any>();
  const [formData, setFormData] = useState<any>({
    packages_count: 1,
    tour_id: dealTours.selected_tour.id,
    pickup_date: dayjs(deal.earliest_pickup_date).format(DATE_FORMAT_ISO),
  });

  const getBookingDetails = (params) => {
    dispatch(setLoading(true));
    axios
      .get(API.tours.bookingDetails.replace('{tour_id}', params.tour_id), {
        params: {
          with_motorcycle: true,
          deal_id: deal.id,
          packages_count: params.packages_count,
          occurrence_id: params.occurrence_id,
          package_id: params.package_id,
          pickup_date:
            params.pickup_date ||
            dayjs(deal.earliest_pickup_date).format(DATE_FORMAT_ISO),
        },
      })
      .then((res) => {
        setWidgetData(res.data);
        setFormData((prev) => ({
          ...prev,
          occurrence_id: res.data.occurrence_id,
          package_id: res.data.selected_package_id,
          pickup_date: res.data.pickup_date,
          errors: null,
        }));
      })
      .catch((error) => {
        switch (error.response.status) {
          case 403:
            setWidgetData((prev) => ({
              ...prev,
              packages: [],
            }));
            dispatch(
              setSnackbar({
                open: true,
                severity: 'error',
                message: error.response.data.error_message,
              }),
            );
            break;
          default:
            handleErrorResponse(error, false);
        }
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };
  const handleBookTour = () => {
    dispatch(setLoading(true));
    axios
      .post(API.tours.bookingDetails.replace('{tour_id}', formData.tour_id), {
        deal_id: deal.id,
        authenticity_token: appState.authenticity_token,
        packages_count: formData.packages_count,
        occurrence_id: formData.occurrence_id,
        package_id: formData.package_id,
        pickup_date:
          formData.pickup_date || widgetData.calendar_options.min_date,
        availability_id: formData.availability_id,
        user_agent: window?.navigator?.userAgent,
      })
      .then((res) => {
        (setCookie as any)('reservation', res.data.reservation.identifier, {
          path: '/',
          sameSite: 'none',
          secure: true,
        });

        navigateToNextPage(res.data.reservation);
      })
      .catch((error) => {
        switch (error.response.status) {
          case 406:
            setFormData((prev) => ({
              ...prev,
              errors: error.response.data,
            }));
            break;
          case 403:
            dispatch(
              setSnackbar({
                open: true,
                severity: 'error',
                message: error.response.data.error_message,
              }),
            );
            break;
          default:
            handleErrorResponse(error, false);
        }
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };
  useEffect(() => {
    getBookingDetails(formData);
  }, []);

  const handleFormDataChange = (key, value) => {
    let newFormtData;
    switch (key) {
      case WIDGET_KEYS.PICKUP_DATE:
        newFormtData = {
          ...formData,
          [key]: dayjs(value).format(DATE_FORMAT_ISO),
        };
        setFormData(newFormtData);
        getBookingDetails(newFormtData);
        break;
      case WIDGET_KEYS.OCCURRENCE_ID:
        // eslint-disable-next-line no-case-declarations
        const targetOccurence = widgetData.occurrences.find(
          (occ) => occ.id === value,
        );
        newFormtData = {
          ...formData,
          occurrence_id: targetOccurence.id,
          pickup_date: targetOccurence.start_date,
        };
        setFormData(newFormtData);
        getBookingDetails(newFormtData);
        break;
      case WIDGET_KEYS.TOUR_ID:
        newFormtData = {
          ...formData,
          [key]: value,
          pickup_date: null,
        };
        setFormData(newFormtData);
        getBookingDetails(newFormtData);
        break;
      default:
        setFormData((prev) => ({
          ...prev,
          [key]: value,
          errors: null,
        }));
    }
  };

  const { t } = useTranslation('fe_er_tour_package_selector');
  const isMultiDayOffroad = widgetData?.tour?.is_multi_day_offroad;

  const getOccurrenceName = (occurrence: any, selected = false) => {
    const date = dayjs(occurrence.start_date).format('MMM D, YYYY');
    const lang = occurrence.language ? `(${occurrence.language.name})` : '';
    const notes = occurrence.notes ? `(${occurrence.notes})` : '';

    const titleTextParts = [date, lang, notes];

    let badgeText = '';
    let badgeStyle = { display: 'inline-block' };
    let badgeClass = 'TBWOccurrenceBadge';

    if (occurrence.soldout) {
      badgeText = `(${t('fe_er_tour_package_selector:sold_out')})`;
      badgeClass += ' TBWSoldOutBadge';
    } else if (
      occurrence.maximum_vehicles_allowed
      && occurrence.maximum_vehicles_allowed <= 5
    ) {
      badgeText = `${[
        t('fe_er_tour_package_selector:only'),
        occurrence.maximum_vehicles_allowed,
        isMultiDayOffroad && t('fe_er_tour_package_selector:spots'),
        t('fe_er_tour_package_selector:left'),
      ]
        .filter(Boolean)
        .join(' ')}`;

      badgeText = selected ? badgeText : `(${badgeText})`;
      badgeStyle = { display: 'block' };
    } else if (occurrence.discount_percent) {
      const discount = `- ${occurrence.discount_percent}% ${t(
        'fe_er_tour_package_selector:occurrence_off',
      )}`;
      titleTextParts.splice(1, 0, discount);
    }

    return (
      <>
        <Box component="span">
          {titleTextParts.filter(Boolean).join(' ')}
          &nbsp;
        </Box>
        {badgeText && (
          <Box component="span" className={badgeClass} style={badgeStyle}>
            {badgeText}
          </Box>
        )}
      </>
    );
  };
  const handleShouldDisableDate = (date) => {
    const dayjsDate = dayjs(date);
    let found;
    if (widgetData.calendar_options.disabled_week_days) {
      found = widgetData.calendar_options.disabled_week_days.findIndex(
        (interval) =>
          interval.days.findIndex((day) => day == dayjsDate.get('day')) != -1 &&
          dayjsDate.isSameOrAfter(interval.start) &&
          dayjsDate.isSameOrBefore(interval.end),
      );
      if (found != -1) return true;
    }

    if (widgetData.calendar_options.blackout_intervals) {
      found = widgetData.calendar_options.blackout_intervals.findIndex(
        (interval) =>
          dayjsDate.isSameOrAfter(interval.start) &&
          dayjsDate.isSameOrBefore(interval.end),
      );
      if (found != -1) return true;
    }

    return false;
  };

  return (
    <Box id="tourWidget">
      {!isCrawler && (
        <TourPackageWidget
          widgetData={widgetData}
          formData={formData}
          handleShouldDisableDate={handleShouldDisableDate}
          getOccurrenceName={getOccurrenceName}
          handleFormDataChange={handleFormDataChange}
          handleBookTour={handleBookTour}
          {...props}
        />
      )}
    </Box>
  );
};

export  {TourPackageWidgetWrapper} ;